import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Progress, Tag } from 'antd';
import './dashboardOperations.css';

function ActionQueue({ operations }) {
  const navigate = useNavigate();
  if (!operations) return null;

  const items = [
    // « /commande » n'a jamais existé comme route (audit 14/07, B1) : ces deux
    // raccourcis tombaient sur la page 404. La liste réelle est /listeCommande.
    { label: 'Commandes à préparer',         val: operations.commandes_a_preparer,  link: '/listeCommande',  severity: operations.commandes_a_preparer > 0  ? 'warn'     : 'ok' },
    { label: 'Paires à remettre aux livreurs', val: operations.paires_a_remettre,   link: '/listeCommande',  severity: operations.paires_a_remettre > 0     ? 'info'     : 'ok' },
    { label: 'Livraisons en retard',          val: operations.livraisons_en_retard,  link: '/livraison', severity: operations.livraisons_en_retard > 0   ? 'critical' : 'ok' },
    { label: 'Retours à traiter',             val: operations.retours_a_traiter,     link: '/livraison', severity: operations.retours_a_traiter > 0      ? 'warn'     : 'ok' },
    { label: 'Échanges en cours',             val: operations.echanges_en_cours,     link: '/livraison', severity: operations.echanges_en_cours > 0      ? 'info'     : 'ok' }
  ];

  return (
    <div className="ops-queue">
      {items.map((it, i) => (
        <button
          key={i}
          className={`ops-queue__item ops-queue__item--${it.severity}`}
          onClick={() => navigate(it.link)}
        >
          <span className="ops-queue__val">{it.val}</span>
          <span className="ops-queue__label">{it.label}</span>
          <span className="ops-queue__arrow">→</span>
        </button>
      ))}
    </div>
  );
}

function StockGauges({ stock }) {
  if (!stock) return null;
  const max = stock.comptable_total || 1;
  return (
    <div className="ops-stock">
      <div className="ops-stock__total">
        <span className="ops-stock__total-val">{(stock.comptable_total || 0).toLocaleString('fr-CD')}</span>
        <span className="ops-stock__total-label">paires comptables</span>
      </div>
      <div className="ops-stock__gauges">
        <div className="ops-gauge-row">
          <span className="ops-gauge-label">Disponible</span>
          <Progress percent={Math.round((stock.disponible_total || 0) / max * 100)} strokeColor="var(--ndoe-success)" size="small" showInfo={false} />
          <span className="ops-gauge-val">{stock.disponible_total || 0}</span>
        </div>
        <div className="ops-gauge-row">
          <span className="ops-gauge-label">Réservé</span>
          <Progress percent={Math.round((stock.reservee_total || 0) / max * 100)} strokeColor="var(--ndoe-gold)" size="small" showInfo={false} />
          <span className="ops-gauge-val">{stock.reservee_total || 0}</span>
        </div>
        <div className="ops-gauge-row">
          <span className="ops-gauge-label">En circulation</span>
          <Progress percent={Math.round((stock.en_circulation || 0) / max * 100)} strokeColor="var(--ndoe-royal)" size="small" showInfo={false} />
          <span className="ops-gauge-val">{stock.en_circulation || 0}</span>
        </div>
      </div>
      <div className="ops-stock__badges">
        {stock.ruptures > 0 && <Tag color="red">{stock.ruptures} rupture{stock.ruptures > 1 ? 's' : ''}</Tag>}
        {stock.faible_stock > 0 && <Tag color="orange">{stock.faible_stock} stock faible</Tag>}
        {stock.sur_engagement > 0 && <Tag color="purple">{stock.sur_engagement} sur-engagement</Tag>}
        {stock.ruptures === 0 && stock.faible_stock === 0 && <Tag color="green">Stock opérationnel</Tag>}
      </div>
    </div>
  );
}

function Tendances({ tendances }) {
  if (!tendances) return null;
  const maxPt = (tendances.top_pointures && tendances.top_pointures[0]?.qte_30j) || 1;
  const maxC  = (tendances.top_couleurs  && tendances.top_couleurs[0]?.qte_30j)  || 1;

  return (
    <div className="ops-tendances">
      <div className="ops-tendances__kpis">
        <div className="ops-tend-kpi">
          <span className="ops-tend-kpi__val">{tendances.ventes_nettes_30j}</span>
          <span className="ops-tend-kpi__label">paires vendues (30j)</span>
        </div>
        <div className="ops-tend-kpi">
          <span className="ops-tend-kpi__val">{tendances.receptions_30j}</span>
          <span className="ops-tend-kpi__label">paires reçues (30j)</span>
        </div>
      </div>

      {tendances.top_pointures && tendances.top_couleurs && (
        <div className="ops-tendances__rankings">
          <div className="ops-ranking">
            <div className="ops-ranking__title">Top pointures (30j)</div>
            {tendances.top_pointures.map((p, i) => (
              <div key={i} className="ops-rank-row">
                <span className="ops-rank-num">{i + 1}</span>
                <span className="ops-rank-name" title={String(p.pointure)}>{p.pointure}</span>
                <div className="ops-rank-bar-wrap">
                  <div className="ops-rank-bar ops-rank-bar--gold" style={{ width: `${Math.round(p.qte_30j / maxPt * 100)}%` }} />
                </div>
                <span className="ops-rank-val">{p.qte_30j}</span>
              </div>
            ))}
          </div>

          <div className="ops-ranking">
            <div className="ops-ranking__title">Top couleurs (30j)</div>
            {tendances.top_couleurs.map((c, i) => (
              <div key={i} className="ops-rank-row">
                <span className="ops-rank-num">{i + 1}</span>
                <span className="ops-rank-name" title={c.couleur}>{c.couleur}</span>
                <div className="ops-rank-bar-wrap">
                  <div className="ops-rank-bar ops-rank-bar--sage" style={{ width: `${Math.round(c.qte_30j / maxC * 100)}%` }} />
                </div>
                <span className="ops-rank-val">{c.qte_30j}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AlertsOps({ alerts }) {
  const navigate = useNavigate();
  if (!alerts || alerts.length === 0) return (
    <div className="ops-no-alerts">Aucune alerte opérationnelle ✓</div>
  );
  return (
    <div className="ops-alerts">
      {alerts.map((a, i) => (
        <button
          key={i}
          className={`ops-alert-item ops-alert-item--${a.severity}`}
          onClick={() => a.link && navigate(a.link)}
          disabled={!a.link}
          title={a.link ? undefined : 'Aucun détail disponible pour cette alerte'}
        >
          <span>{a.message}</span>
          {a.link && <span>→</span>}
        </button>
      ))}
    </div>
  );
}

function QuickLinks() {
  const navigate = useNavigate();
  const links = [
    { label: 'Commandes', path: '/listeCommande' },
    { label: 'Livraisons', path: '/livraison' },
    { label: 'Stock',      path: '/journal_stock' },
    { label: 'Produits',   path: '/varianteProduit' }
  ];
  return (
    <div className="ops-quick-links">
      {links.map((l, i) => (
        <button key={i} className="ops-quick-link" onClick={() => navigate(l.path)}>
          {l.label}
        </button>
      ))}
    </div>
  );
}

export default function DashboardOperations({ data }) {
  if (!data) return null;
  const { operations, stock, tendances, alerts = [] } = data;

  return (
    <div className="ops-root">
      <div className="ops-header">
        <h1 className="ops-header__title">Tableau de bord opérationnel</h1>
        <QuickLinks />
      </div>

      <div className="ops-content">
        <section className="ops-section">
          <h2 className="ops-section__title">File d'action</h2>
          <ActionQueue operations={operations} />
        </section>

        <section className="ops-section">
          <h2 className="ops-section__title">Stock (paires)</h2>
          <StockGauges stock={stock} />
        </section>

        <section className="ops-section">
          <h2 className="ops-section__title">Tendances — 30 jours</h2>
          <Tendances tendances={tendances} />
        </section>

        {alerts.length > 0 && (
          <section className="ops-section">
            <h2 className="ops-section__title">Alertes</h2>
            <AlertsOps alerts={alerts} />
          </section>
        )}
      </div>
    </div>
  );
}
